import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../atoms';
import { MenuQuery, OperatorQuery } from '../../../services/query';
import { EndpointOption, RoleOption } from '../../../types/models/roleAuthorization.models';
import { PageUrl } from '../../../types/enums/pageUrl-enums';

interface IProps {
    open?: boolean;
    onClose?: () => void;
}

const MIN_SEARCH_LENGTH = 2;
const SEARCH_CONTROL_HEIGHT = 45;
const EDIT_USER_ROLE_NAME_STORAGE_KEY = (roleId: number | string) => `editUser:roleName:${roleId}`;

const getEndpointOptionKey = (option: EndpointOption) => `${option.httpmethod ?? ''}::${option.authorizedRoute ?? ''}`;

const mapEndpointItem = (item: Record<string, unknown>): EndpointOption | null => {
    const authorizedRoute = String(item.authorizedRoute ?? item.AuthorizedRoute ?? '').trim();
    if (!authorizedRoute) return null;

    return {
        authorizedRoute,
        httpmethod: String(item.httpmethod ?? item.HttpMethod ?? item.httpMethod ?? ''),
        privilege: String(item.privilege ?? item.Privilege ?? ''),
    };
};

const matchesEndpointSearch = (option: EndpointOption, search: string) => {
    const normalizedSearch = search.trim().toLowerCase();
    if (normalizedSearch.length < MIN_SEARCH_LENGTH) return false;

    return option.authorizedRoute.toLowerCase().includes(normalizedSearch);
};

const normalizeEndpoints = (response: unknown): EndpointOption[] => {
    const rawList = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: unknown })?.data)
          ? (response as { data: Record<string, unknown>[] }).data
          : [];

    return rawList
        .map((item) => mapEndpointItem(item as Record<string, unknown>))
        .filter((item): item is EndpointOption => item !== null);
};

const normalizeRoles = (response: unknown): RoleOption[] => {
    const data = (response as { model?: { data?: RoleOption[] } })?.model?.data;
    if (Array.isArray(data)) return data;

    if (Array.isArray(response)) return response as RoleOption[];

    return [];
};

export const SCUIEndpointSearchDialog: FC<IProps> = ({ open = false, onClose }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

    const handleGotoEditUser = (role: RoleOption) => {
        localStorage.setItem(EDIT_USER_ROLE_NAME_STORAGE_KEY(role.roleId), role.roleNameKey);
        window.open(`${PageUrl.editUser.url}/?RoleId=${role.roleId}`, '_blank', 'noopener,noreferrer');
    };

    const [endpoints, setEndpoints] = useState<EndpointOption[]>([]);
    const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointOption | null>(null);
    const [endpointSearch, setEndpointSearch] = useState('');
    const [matchedRoles, setMatchedRoles] = useState<RoleOption[] | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoadingEndpoints, setIsLoadingEndpoints] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const canSearch = endpointSearch.trim().length >= MIN_SEARCH_LENGTH;

    const resetForm = useCallback(() => {
        setSelectedEndpoint(null);
        setEndpointSearch('');
        setMatchedRoles(null);
        setHasSearched(false);
        setIsSearching(false);
    }, []);

    const filteredEndpoints = useMemo(() => {
        if (!canSearch) return [];

        return endpoints.filter((option) => matchesEndpointSearch(option, endpointSearch));
    }, [canSearch, endpoints, endpointSearch]);

    const endpointAutocompleteValue = useMemo(() => {
        if (!selectedEndpoint) return null;

        return filteredEndpoints.some(
            (option) => getEndpointOptionKey(option) === getEndpointOptionKey(selectedEndpoint)
        )
            ? selectedEndpoint
            : null;
    }, [filteredEndpoints, selectedEndpoint]);

    const loadEndpoints = useCallback(async () => {
        setIsLoadingEndpoints(true);
        try {
            const endpointsResponse = await MenuQuery.GetMenuAllEndPoints();
            if (endpointsResponse) {
                setEndpoints(normalizeEndpoints(endpointsResponse));
            }
        } catch (error) {
            console.error('Endpoint search load error:', error);
            Swal.fire(t('authorizationManagement.endpointSearch.loadError'), '', 'error');
        } finally {
            setIsLoadingEndpoints(false);
        }
    }, [t]);

    useEffect(() => {
        if (open) {
            loadEndpoints();
        } else {
            resetForm();
        }
    }, [open, loadEndpoints, resetForm]);

    const handleClose = () => {
        if (!isSearching) {
            onClose?.();
        }
    };

    const handleSearch = async () => {
        if (!canSearch) return;

        if (!selectedEndpoint?.authorizedRoute?.trim()) {
            Swal.fire(t('authorizationManagement.endpointSearch.selectEndpointRequired'), '', 'warning');
            return;
        }

        setIsSearching(true);
        setHasSearched(true);
        try {
            const response = await OperatorQuery.SearchRolesByEndpoint(selectedEndpoint.authorizedRoute);
            setMatchedRoles(normalizeRoles(response));
        } catch (error) {
            console.error('Endpoint role search error:', error);
            setMatchedRoles([]);
            Swal.fire(t('authorizationManagement.endpointSearch.searchError'), '', 'error');
        } finally {
            setIsSearching(false);
        }
    };

    const renderResults = () => {
        if (!hasSearched) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        py: 4,
                        px: 2,
                        textAlign: 'center',
                        minHeight: 140,
                    }}
                >
                    <CodeIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                        {t('authorizationManagement.endpointSearch.emptyState')}
                    </Typography>
                </Box>
            );
        }

        if (isSearching) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!matchedRoles || matchedRoles.length === 0) {
            return (
                <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="warning.main">
                        {t('authorizationManagement.endpointSearch.noResults')}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        letterSpacing: 0.5,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                    }}
                >
                    {t('authorizationManagement.endpointSearch.resultsTitle')}
                </Typography>
                {matchedRoles.map((role) => (
                    <Box
                        key={role.roleId}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            width: '100%',
                            px: 2,
                            py: 1.5,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography
                            component="button"
                            type="button"
                            onClick={() => handleGotoEditUser(role)}
                            sx={{
                                all: 'unset',
                                cursor: 'pointer',
                                fontWeight: 600,
                                wordBreak: 'break-word',
                                minWidth: 0,
                                color: 'primary.main',
                                textDecoration: 'underline',
                                textDecorationColor: 'transparent',
                                '&:hover': {
                                    textDecorationColor: 'inherit',
                                },
                            }}
                        >
                            {role.roleNameKey}
                        </Typography>
                        <Chip
                            label={t('authorizationManagement.endpointSearch.authorized')}
                            size="small"
                            sx={{
                                flexShrink: 0,
                                bgcolor: '#E3F2FD',
                                color: 'primary.main',
                                fontWeight: 700,
                                border: 'none',
                            }}
                        />
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <SearchIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography
                    variant="h5"
                    sx={{
                        flex: 1,
                        fontWeight: 700,
                        fontSize: { xs: '1.35rem', sm: '1.5rem' },
                        lineHeight: 1.3,
                    }}
                >
                    {t('authorizationManagement.endpointSearch.title')}
                </Typography>
                <IconButton
                    aria-label={t('general.close')}
                    onClick={handleClose}
                    disabled={isSearching}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {isLoadingEndpoints ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isPhone ? 'column' : 'row',
                                alignItems: isPhone ? 'stretch' : 'flex-end',
                                gap: 1.5,
                            }}
                        >
                            <Autocomplete
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    '& .MuiOutlinedInput-root': {
                                        height: SEARCH_CONTROL_HEIGHT,
                                        boxSizing: 'border-box',
                                    },
                                }}
                                options={filteredEndpoints}
                                value={endpointAutocompleteValue}
                                inputValue={endpointSearch}
                                openOnFocus={false}
                                onInputChange={(_, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setEndpointSearch(newInputValue);
                                        setSelectedEndpoint(null);
                                        setMatchedRoles(null);
                                        setHasSearched(false);
                                        return;
                                    }

                                    if (reason === 'clear') {
                                        setEndpointSearch('');
                                        setSelectedEndpoint(null);
                                        setMatchedRoles(null);
                                        setHasSearched(false);
                                        return;
                                    }

                                    if (reason === 'reset' && selectedEndpoint) {
                                        setEndpointSearch(selectedEndpoint.authorizedRoute);
                                    }
                                }}
                                onChange={(_, value) => {
                                    setSelectedEndpoint(value);
                                    setEndpointSearch(value?.authorizedRoute ?? '');
                                    setMatchedRoles(null);
                                    setHasSearched(false);
                                }}
                                getOptionLabel={(option) => option.authorizedRoute ?? ''}
                                isOptionEqualToValue={(option, value) =>
                                    getEndpointOptionKey(option) === getEndpointOptionKey(value)
                                }
                                filterOptions={(options) => options}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;

                                    return (
                                        <li key={key ?? getEndpointOptionKey(option)} {...optionProps}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    width: '100%',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    sx={{ fontWeight: 700, minWidth: 56, fontSize: 12, flexShrink: 0 }}
                                                >
                                                    {option.httpmethod}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: 12,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {option.authorizedRoute}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('authorizationManagement.endpointSearch.endpointLabel')}
                                        placeholder={t('authorizationManagement.endpointSearch.endpointPlaceholder')}
                                        autoComplete="off"
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                ...params.inputProps,
                                                autoComplete: 'new-password',
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: isPhone ? '100%' : 'auto',
                                    alignSelf: isPhone ? 'stretch' : 'flex-end',
                                    height: SEARCH_CONTROL_HEIGHT,
                                    '& .sc-btn.MuiButton-root': {
                                        height: `${SEARCH_CONTROL_HEIGHT}px !important`,
                                        minHeight: `${SEARCH_CONTROL_HEIGHT}px !important`,
                                        maxHeight: `${SEARCH_CONTROL_HEIGHT}px !important`,
                                        width: isPhone ? '100%' : 'auto',
                                        px: '16px !important',
                                        boxSizing: 'border-box',
                                    },
                                    '& .sc-btn__text': {
                                        padding: '0 !important',
                                        margin: 0,
                                        lineHeight: 1.2,
                                    },
                                }}
                            >
                                <SCUIButton
                                    text={t('authorizationManagement.endpointSearch.searchInRoles')}
                                    type="primary"
                                    sizeType="default"
                                    size="m"
                                    onClick={handleSearch}
                                    disabled={!canSearch || isSearching}
                                />
                            </Box>
                        </Box>

                        {renderResults()}
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'flex-end',
                }}
            >
                <SCUIButton
                    text={t('general.cancel')}
                    type="secondary"
                    sizeType="fixed"
                    size="m"
                    onClick={handleClose}
                    disabled={isSearching}
                />
            </DialogActions>
        </Dialog>
    );
};
